import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from '../app/axiosInterceptor';
import { HTTP_GETTYPE, STATUS_API, TIMEOUT_DEFAULT, messageToastType_const } from 'src/app/constant/config';
import { MESSAGE } from 'src/app/constant/message';
import AxiosAdapter from './AxiosAdapter';
import { _convertObjectToQuery } from 'src/app/utils/apiService';
import { showGlobalToast } from './uiSlice';

// ─── Product CRUD ─────────────────────────────────────────────────────────────
export const getListProducts = AxiosAdapter.GetHttp(
  'productSlice/getListProducts',
  '/product',
  HTTP_GETTYPE.ALL_PAGINATION
);

export const getProductById = AxiosAdapter.HttpGetById(
  'productSlice/getProductById',
  '/product'
);

export const createProduct = AxiosAdapter.HttpPost(
  'productSlice/createProduct',
  '/product',
  MESSAGE.CREATE_PRODUCT_SUCCESS,
  MESSAGE.CREATE_PRODUCT_FAIL
);

export const updateProduct = AxiosAdapter.HttpUpdateById(
  'productSlice/updateProduct',
  '/product',
  MESSAGE.UPDATE_PRODUCT_SUCCESS,
  MESSAGE.UPDATE_PRODUCT_FAIL
);

export const deleteProduct = AxiosAdapter.HttpDelete(
  'productSlice/deleteProduct',
  'product',
  MESSAGE.DELETE_PRODUCT_SUCCESS,
  MESSAGE.DELETE_PRODUCT_FAIL
);

// ─── Stock ────────────────────────────────────────────────────────────────────
export const getProductStocks = AxiosAdapter.HttpGetById(
  'productSlice/getProductStocks',
  '/product'
);

export const getStockTransactions = AxiosAdapter.GetHttp(
  'productSlice/getStockTransactions',
  '/product/stock/transactions',
  HTTP_GETTYPE.ALL_PAGINATION
);

export const adjustStock = AxiosAdapter.HttpPost(
  'productSlice/adjustStock',
  '/product/stock/adjust',
  MESSAGE.ADJUST_STOCK_SUCCESS,
  MESSAGE.ADJUST_STOCK_FAIL
);

export const transferStock = AxiosAdapter.HttpPost(
  'productSlice/transferStock',
  '/product/stock/transfer',
  MESSAGE.TRANSFER_STOCK_SUCCESS,
  MESSAGE.TRANSFER_STOCK_FAIL
);

// ─── Images ───────────────────────────────────────────────────────────────────
export const uploadProductImages = createAsyncThunk(
  'productSlice/uploadProductImages',
  async ({ id, images }, { dispatch, rejectWithValue }) => {
    try {
      const formData = new FormData();
      images.forEach(img => formData.append('images', img));
      const response = await axios({
        url: `${process.env.REACT_APP_BACKEND_URL}/product/${id}/images`,
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: 'Bearer ' + localStorage.getItem('access-token')
        },
        data: formData,
        timeout: TIMEOUT_DEFAULT
      });
      dispatch(
        showGlobalToast({ message: 'Upload ảnh thành công!', type: messageToastType_const.success })
      );
      return response.data;
    } catch (err) {
      dispatch(
        showGlobalToast({ message: 'Upload ảnh thất bại!', type: messageToastType_const.error })
      );
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteProductImage = AxiosAdapter.HttpDelete(
  'productSlice/deleteProductImage',
  'product/images',
  'Xóa ảnh thành công',
  'Xóa ảnh thất bại'
);

// ─── Excel Import / Template ──────────────────────────────────────────────────
export const downloadImportTemplate = createAsyncThunk(
  'productSlice/downloadImportTemplate',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BACKEND_URL}/product/import/template`,
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('access-token')
        },
        responseType: 'blob',
        timeout: TIMEOUT_DEFAULT
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'mau_nhap_san_pham.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const importProductsFromExcel = createAsyncThunk(
  'productSlice/importProductsFromExcel',
  async ({ file, agency_id }, { dispatch, rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (agency_id) formData.append('agency_id', agency_id);

      const response = await axios({
        url: `${process.env.REACT_APP_BACKEND_URL}/product/import`,
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: 'Bearer ' + localStorage.getItem('access-token')
        },
        data: formData,
        responseType: 'blob',
        timeout: 30000
      });

      // Extract summary from response headers
      const created = response.headers['x-import-created'] || 0;
      const updated = response.headers['x-import-updated'] || 0;
      const quantity = response.headers['x-import-quantity'] || 0;
      const errorCount = response.headers['x-import-error'] || 0;

      // Auto-download the report blob
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bao_cao_nhap_kho.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      dispatch(
        showGlobalToast({
          message: `Nhập kho thành công! Tạo mới: ${created}, Cập nhật: ${updated}, Lỗi: ${errorCount}`,
          type: messageToastType_const.success
        })
      );
      return { created: Number(created), updated: Number(updated), quantity: Number(quantity), errorCount: Number(errorCount) };
    } catch (err) {
      dispatch(
        showGlobalToast({ message: 'Nhập kho từ Excel thất bại!', type: messageToastType_const.error })
      );
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ─── Order Products ───────────────────────────────────────────────────────────
export const getOrderProducts = AxiosAdapter.GetHttp(
  'productSlice/getOrderProducts',
  '/order/',
  HTTP_GETTYPE.DETAIL
);

// ─── Slice ────────────────────────────────────────────────────────────────────
const productSlice = createSlice({
  name: 'productSlice',
  initialState: {
    listProducts: [],
    total: 0,
    currentProduct: null,
    productStocks: [],
    stockTransactions: [],
    transactionTotal: 0,
    orderProducts: [],
    importResult: null,
    isLoading: false,
    isLoadingStock: false,
    isLoadingTransactions: false,
    statusGet: null,
    statusCreate: null,
    statusUpdate: null,
    statusDelete: null,
    statusAdjust: null,
    statusTransfer: null,
    statusImport: null,
    error: null,
    statistic: {}
  },
  reducers: {
    resetChange: state => {
      state.statusCreate = null;
      state.statusUpdate = null;
      state.statusDelete = null;
      state.statusAdjust = null;
      state.statusTransfer = null;
      state.statusImport = null;
      state.error = null;
      state.importResult = null;
    },
    clearOrderProducts: state => {
      state.orderProducts = [];
    }
  },
  extraReducers: {
    // getListProducts
    [getListProducts.pending]: state => {
      state.statusGet = STATUS_API.PENDING;
      state.isLoading = true;
    },
    [getListProducts.fulfilled]: (state, action) => {
      state.statusGet = STATUS_API.SUCCESS;
      state.isLoading = false;
      const payload = action.payload?.payload;
      const resolved = payload?.data?.products || payload?.products || payload?.data || payload || [];
      state.listProducts = Array.isArray(resolved) ? resolved : [];
      state.total = payload?.numberOfItem || payload?.total || state.listProducts.length;
      state.statistic = payload?.statistic || {};
    },
    [getListProducts.rejected]: (state, action) => {
      state.statusGet = STATUS_API.ERROR;
      state.isLoading = false;
      state.error = action.payload?.message || action.error;
    },

    // getProductById
    [getProductById.fulfilled]: (state, action) => {
      state.currentProduct = action.payload?.payload?.product || action.payload?.payload || null;
    },

    // createProduct
    [createProduct.pending]: state => { state.statusCreate = STATUS_API.PENDING; },
    [createProduct.fulfilled]: state => { state.statusCreate = STATUS_API.SUCCESS; },
    [createProduct.rejected]: (state, action) => {
      state.statusCreate = STATUS_API.ERROR;
      state.error = action.payload?.message;
    },

    // updateProduct
    [updateProduct.pending]: state => { state.statusUpdate = STATUS_API.PENDING; },
    [updateProduct.fulfilled]: state => { state.statusUpdate = STATUS_API.SUCCESS; },
    [updateProduct.rejected]: (state, action) => {
      state.statusUpdate = STATUS_API.ERROR;
      state.error = action.payload?.message;
    },

    // deleteProduct
    [deleteProduct.pending]: state => { state.statusDelete = STATUS_API.PENDING; },
    [deleteProduct.fulfilled]: state => { state.statusDelete = STATUS_API.SUCCESS; },
    [deleteProduct.rejected]: (state, action) => {
      state.statusDelete = STATUS_API.ERROR;
      state.error = action.payload?.message;
    },

    // getProductStocks
    [getProductStocks.pending]: state => { state.isLoadingStock = true; },
    [getProductStocks.fulfilled]: (state, action) => {
      state.isLoadingStock = false;
      state.productStocks = action.payload?.payload?.stocks || action.payload?.payload || [];
    },
    [getProductStocks.rejected]: state => { state.isLoadingStock = false; },

    // getStockTransactions
    [getStockTransactions.pending]: state => { state.isLoadingTransactions = true; },
    [getStockTransactions.fulfilled]: (state, action) => {
      state.isLoadingTransactions = false;
      const payload = action.payload?.payload;
      state.stockTransactions = payload?.transactions || payload?.data || [];
      state.transactionTotal = payload?.numberOfItem || payload?.total || state.stockTransactions.length;
    },
    [getStockTransactions.rejected]: state => { state.isLoadingTransactions = false; },

    // adjustStock
    [adjustStock.pending]: state => { state.statusAdjust = STATUS_API.PENDING; },
    [adjustStock.fulfilled]: state => { state.statusAdjust = STATUS_API.SUCCESS; },
    [adjustStock.rejected]: (state, action) => {
      state.statusAdjust = STATUS_API.ERROR;
      state.error = action.payload?.message;
    },

    // transferStock
    [transferStock.pending]: state => { state.statusTransfer = STATUS_API.PENDING; },
    [transferStock.fulfilled]: state => { state.statusTransfer = STATUS_API.SUCCESS; },
    [transferStock.rejected]: (state, action) => {
      state.statusTransfer = STATUS_API.ERROR;
      state.error = action.payload?.message;
    },

    // importProductsFromExcel
    [importProductsFromExcel.pending]: state => { state.statusImport = STATUS_API.PENDING; },
    [importProductsFromExcel.fulfilled]: (state, action) => {
      state.statusImport = STATUS_API.SUCCESS;
      state.importResult = action.payload;
    },
    [importProductsFromExcel.rejected]: (state, action) => {
      state.statusImport = STATUS_API.ERROR;
      state.error = action.payload?.message;
    },

    // getOrderProducts
    [getOrderProducts.fulfilled]: (state, action) => {
      state.orderProducts = action.payload?.payload || [];
    },
    [getOrderProducts.rejected]: state => {
      state.orderProducts = [];
    }
  }
});

export const { resetChange, clearOrderProducts } = productSlice.actions;
export default productSlice.reducer;
